import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiTrendingUp, FiList } from 'react-icons/fi';
// Stacked: became_job (green) + remaining (indigo); lost shown as a label.
const SERIES = [
  { key: 'became_job', label: 'Became Job', color: '#10B981' },
  { key: 'open',       label: 'Open',       color: '#6366F1' },
];

const GROUPS = [
  { key: 'month',   label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year',    label: 'Year' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// Stacked SVG columns per period with a month/quarter/year toggle.
// Clicking a column drills into that period's records.
const QuotationTrendChart = ({ data, loading, error, groupBy, onGroupByChange, onDrillDown, title }) => {
  const [tooltip, setTooltip] = useState(null);
  const [view, setView] = useState('chart');
  const rows = (data || []).map(r => ({ ...r, open: Math.max((r.total || 0) - (r.became_job || 0), 0) }));
  const maxTotal = Math.max(...rows.map(r => r.total), 1);

  const chartH = 160, barW = 34, gap = 56;
  const width = Math.max(rows.length * gap + 20, 300);

  const totals = rows.reduce((s, r) => ({
    total:      s.total      + (r.total      || 0),
    became_job: s.became_job + (r.became_job || 0),
    open:       s.open       + (r.open       || 0),
    lost:       s.lost       + (r.lost       || 0),
    weight:     s.weight     + (r.weight     || 0),
  }), { total: 0, became_job: 0, open: 0, lost: 0, weight: 0 });

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiTrendingUp size={15} className="tw-text-primary-500" />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{title}</h3>
          <div className="tw-flex tw-items-center tw-gap-3 tw-ml-3">
            {SERIES.map(s => (
              <span key={s.key} className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-500">
                <span className="tw-w-2 tw-h-2 tw-rounded-sm" style={{ background: s.color }} />{s.label}
              </span>
            ))}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
            <button type="button" onClick={() => setView('chart')}
              className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 ${view === 'chart' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}>Chart</button>
            <button type="button" onClick={() => setView('table')}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}><FiList size={11} /> Table</button>
          </div>
          <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
            {GROUPS.map(g => (
              <button key={g.key} type="button" onClick={() => onGroupByChange(g.key)}
                className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer tw-border-0 ${
                  groupBy === g.key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
                }`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || rows.length === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10 tw-mb-0">No data for this period</p>
      ) : view === 'table' ? (
        <div className="tw-overflow-auto tw-max-h-[420px]">
          <table className="tw-w-full tw-text-xs tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Period</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600">Total</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-emerald-600">Became Job</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-primary-600">Open</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-red-500">Lost</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-cyan-600">Weight (MT)</th>
                <th className="tw-px-3 tw-py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.period}
                  onClick={() => onDrillDown && onDrillDown(r, groupBy)}
                  className={`tw-border-b tw-border-gray-100 ${onDrillDown ? 'tw-cursor-pointer' : ''} ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-primary-50/60`}>
                  <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{r.label}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-gray-900">{fmt(r.total)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-emerald-600">{fmt(r.became_job)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{fmt(r.open)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{r.lost ? fmt(r.lost) : '—'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-cyan-600">{fmt(Math.round(r.weight))}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-primary-400">{onDrillDown ? '↗' : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs">Total ({rows.length})</td>
                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-900">{fmt(totals.total)}</td>
                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600">{fmt(totals.became_job)}</td>
                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{fmt(totals.open)}</td>
                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{fmt(totals.lost)}</td>
                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-cyan-600">{fmt(Math.round(totals.weight))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="tw-p-5 tw-overflow-x-auto tw-relative">
          <svg width={width} height={chartH + 50}>
            {rows.map((r) => {
              const x = rows.indexOf(r) * gap + 16;
              let yCursor = chartH;
              const segments = SERIES.map(s => {
                const h = (r[s.key] / maxTotal) * chartH;
                yCursor -= h;
                return { ...s, h, y: yCursor, value: r[s.key] };
              });
              return (
                <g key={r.period}
                  onMouseEnter={() => setTooltip({ r })}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => onDrillDown && onDrillDown(r, groupBy)}
                  style={onDrillDown ? { cursor: 'pointer' } : undefined}>
                  {segments.map(seg => seg.h > 0 && (
                    <rect key={seg.key} x={x} y={seg.y} width={barW} height={seg.h}
                      fill={seg.color} opacity={0.85} rx={2}>
                      <title>{`${r.label} — ${seg.label}: ${fmt(seg.value)}`}</title>
                    </rect>
                  ))}
                  <text x={x + barW / 2} y={yCursor - 5} textAnchor="middle" fontSize="10" fontWeight="700" className="tw-fill-gray-700">
                    {fmt(r.total)}
                  </text>
                  <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize="9" className="tw-fill-gray-400">
                    {r.label}
                  </text>
                  {r.lost > 0 && (
                    <text x={x + barW / 2} y={chartH + 30} textAnchor="middle" fontSize="9" fontWeight="600" fill="#EF4444">
                      {fmt(r.lost)} lost
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {tooltip && (
            <div className="tw-absolute tw-top-2 tw-right-3 tw-bg-slate-800 tw-text-white tw-rounded tw-px-3 tw-py-2 tw-text-[11px] tw-shadow-lg tw-pointer-events-none">
              <p className="tw-font-bold tw-mb-1">{tooltip.r.label}</p>
              <p className="tw-mb-0">Total: {fmt(tooltip.r.total)} · Became Job: {fmt(tooltip.r.became_job)}</p>
              <p className="tw-mb-0">Lost: {fmt(tooltip.r.lost)} · Weight: {fmt(tooltip.r.weight)} MT</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

QuotationTrendChart.propTypes = {
  data: PropTypes.array, loading: PropTypes.bool, error: PropTypes.any,
  groupBy: PropTypes.string.isRequired, onGroupByChange: PropTypes.func.isRequired,
  onDrillDown: PropTypes.func, title: PropTypes.string,
};
QuotationTrendChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null, title: 'Quotation Trend' };

export default QuotationTrendChart;
